import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { InputAdornment } from "@mui/material";
import React, { ReactElement, useEffect, useState } from "react";
import isEqual from "react-fast-compare";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router";
import { ThemeProvider } from "styled-components";

import Filter from "components/Filter";
import Icon from "components/Icon";
import Office from "components/Office";
import Section from "components/Section";
import TextBlock from "components/TextBlock";
import {
    useBreakpoint,
    useDebounce,
    useIsomorphicLayoutEffect,
    useGoogleMapsKey,
} from "hooks";
import { Wrapper } from "style/components/Page";
import { LocationChangeState } from "types/state/LocationChangeState";
import { addEditAttributes } from "utils/episerver";
import { getMapCoordinates } from "utils/getMapsCoordinates";

import {
    Container,
    Header,
    SearchField,
    GpsButton,
    StyledForm,
    Description,
    SearchButton,
    GpsError,
    GpsWrapper,
    NoResults,
    ContentWrapper,
    ResultList,
    ResultMap,
    ResultWrapper,
    ResultButtons,
    FilterButtons,
} from "./OfficeListingPage.styled";
import OfficeListingPageProps from "./OfficeListingPageProps";

const OfficeListingPage = ({
    hero,
    heading,
    result: { filters, items },
    headingSize = "XL",
    theme = "blue",
    contentTheme = "blue",
    actionTheme,
    searchHelp,
    l18n: {
        findUs,
        listButton,
        mapButton,
        noResults,
        searchLabel,
        searchPlaceholder,
        searchByLocationButton,
        searchByLocationErrorMessage,
    },
}: OfficeListingPageProps): ReactElement => {
    const { control, handleSubmit, reset } = useForm();
    const [canGetLocation, setCanGetLocation] = useState(true);
    const [view, setView] = useState<"map" | "list">("list");
    const [searchTerm, setSearchTerm] = useState<string | undefined>();
    const debouncedSearchTerm = useDebounce<string | undefined>(
        searchTerm,
        500,
    );
    const googleMapsKey = useGoogleMapsKey();
    const [markers, setMarkers] = useState<google.maps.MarkerOptions[]>([]);
    const history = useHistory();
    const location = useLocation<LocationChangeState>();
    const sendDataToGTM = useGTMDispatch();
    const hasOffices = items && items.length > 0;

    heading = heading || hero?.heading;

    const handleOnSubmit: SubmitHandler<FieldValues> = (data) => {
        history.push({ search: `?oq=${data.oq}` });
    };

    const triggerSubmit = () => {
        handleOnSubmit({ oq: searchTerm });
    };

    useIsomorphicLayoutEffect(() => {
        if (debouncedSearchTerm !== undefined) triggerSubmit;
    }, [triggerSubmit, debouncedSearchTerm]);

    const handleGeoSearchClick = async () => {
        if (!navigator.geolocation) {
            setCanGetLocation(false);
            return;
        }
        const geoloc = await navigator.permissions.query({
            name: "geolocation",
        });

        if (geoloc.state !== "granted") {
            setCanGetLocation(false);
        }
        navigator.geolocation.getCurrentPosition((pos: GeolocationPosition) => {
            reset();
            if (pos.coords.latitude && pos.coords.longitude) {
                setCanGetLocation(true);
            }
            history.push({
                search: `?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`,
            });
        });
    };

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const queryTerm = query.get("oq");
        const isGeoSearch = query.has("lat") && query.has("lng");

        if (queryTerm) setSearchTerm(queryTerm);
        if (queryTerm || isGeoSearch) {
            sendDataToGTM({
                event: "office_search",
                search_text: queryTerm || "(geo)",
            });
        }
    }, [location, sendDataToGTM]);

    let searchIcon: string;

    useEffect(() => {
        const fetchMarkers = async () => {
            if (!items) return;

            const markerOptionsPromises = items.map(async (item) => {
                let lat = item.latitude;
                let lng = item.longitude;

                if (!lat || !lng) {
                    try {
                        const coordinates = await getMapCoordinates(
                            item.addressRow1 || "",
                            item.city || "",
                            item.postalCode || "",
                            googleMapsKey,
                        );
                        lat = coordinates.lat;
                        lng = coordinates.lng;
                    } catch (error) {
                        process.env.NODE_ENV === "development" &&
                            console.error("Error fetching coordinates:", error);
                    }
                }

                return {
                    title: item.officeName || "",
                    position: { lat: lat || 0, lng: lng || 0 },
                    ...item,
                } as google.maps.MarkerOptions;
            });

            const markerOptions = await Promise.all(markerOptionsPromises);

            const validMarkers = markerOptions.filter((marker) => {
                if (
                    marker.position &&
                    marker.position.lat !== undefined &&
                    marker.position.lng !== undefined
                ) {
                    return (
                        marker.position.lat !== 0 && marker.position.lng !== 0
                    );
                }
                return false;
            });

            setMarkers(validMarkers);
        };

        fetchMarkers();
    }, [items, googleMapsKey]);

    const hasMarkers = markers.length > 0;

    const map =
        hasOffices && hasMarkers
            ? {
                  l18n: { findUs: findUs },
                  useCluster: true,
                  markers: markers,
                  options: {
                      zoom: 18,
                      zoomControl: true,
                      disableDefaultUI: false,
                      mapTypeControl: false,
                      center: {
                          lat: 59.326613552284655,
                          lng: 18.065554634562183,
                      },
                  },
              }
            : undefined;

    switch (useBreakpoint(["XL"])) {
        case "XL":
            searchIcon = "enter56";
            break;
        default:
            searchIcon = "enter42";
    }

    return (
        <Wrapper id="maincontent">
            <ThemeProvider
                theme={{
                    theme: theme,
                    contentTheme: contentTheme,
                    actionTheme: actionTheme || contentTheme,
                }}
            >
                <Section theme={theme} alignItems="center">
                    <Container>
                        <ContentWrapper>
                            {heading && (
                                <Header
                                    $headingSize={headingSize}
                                    {...addEditAttributes("Heading")}
                                >
                                    {heading}
                                </Header>
                            )}
                            {filters && filters.length > 1 && (
                                <FilterButtons>
                                    {filters.map((filter, index) => (
                                        <Filter
                                            key={`Filter${index}`}
                                            selected={filter.selected}
                                            disabled={
                                                typeof filter.count !==
                                                    "undefined" &&
                                                filter.count <= 0
                                            }
                                            url={filter.url}
                                        >
                                            {filter.linkText}
                                        </Filter>
                                    ))}
                                </FilterButtons>
                            )}
                            <StyledForm
                                onSubmit={handleSubmit(handleOnSubmit)}
                                method="get"
                            >
                                <SearchField
                                    control={control}
                                    elementName="oq"
                                    label={searchLabel}
                                    placeHolder={searchPlaceholder}
                                    predefinedValue={searchTerm}
                                    onChange={(e) =>
                                        setSearchTerm(e.target.value || "")
                                    }
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <SearchButton
                                                key={`SearchIcon-${searchIcon}`}
                                                icon={searchIcon}
                                                a11y={true}
                                                iconOnly={true}
                                                type="submit"
                                            >
                                                {searchLabel}
                                            </SearchButton>
                                        </InputAdornment>
                                    }
                                />
                                <GpsWrapper>
                                    <GpsButton
                                        onClick={handleGeoSearchClick}
                                        $hoverSpan={true}
                                    >
                                        <Icon icon="gps28" />
                                        <span>{searchByLocationButton}</span>
                                    </GpsButton>
                                    {!canGetLocation && (
                                        <GpsError>
                                            {searchByLocationErrorMessage}
                                        </GpsError>
                                    )}
                                </GpsWrapper>
                            </StyledForm>

                            <Description {...addEditAttributes("SearchHelp")}>
                                {searchHelp && (
                                    <TextBlock content={searchHelp} />
                                )}
                            </Description>
                        </ContentWrapper>
                        <ResultWrapper>
                            <ResultButtons>
                                <Filter
                                    selected={view === "list"}
                                    onClick={() => setView("list")}
                                >
                                    <Icon icon="list28" />
                                    {listButton || "List"}
                                </Filter>
                                <Filter
                                    selected={view === "map"}
                                    onClick={() => setView("map")}
                                    disabled={!hasMarkers}
                                >
                                    <Icon icon="map28" />
                                    {mapButton || "Map"}
                                </Filter>
                            </ResultButtons>
                            {hasOffices && hasMarkers && view === "map" ? (
                                <ResultMap {...map} />
                            ) : (
                                <ResultList>
                                    {hasOffices ? (
                                        items?.map((item, index) => (
                                            <Office
                                                key={`Office${index}`}
                                                {...item}
                                            />
                                        ))
                                    ) : (
                                        <NoResults>{noResults}</NoResults>
                                    )}
                                </ResultList>
                            )}
                        </ResultWrapper>
                    </Container>
                </Section>
            </ThemeProvider>
        </Wrapper>
    );
};

export default React.memo(OfficeListingPage, isEqual);
